import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { formatCurrency, formatDate } from '@/lib/utils';
import { CheckCircle, Calendar, MapPin, Mail, Download } from 'lucide-react';
import type { RegistrationWizardState, Event } from '@/types';

interface ConfirmationStepProps {
  wizardState: RegistrationWizardState;
  event: Event | undefined;
}

export function ConfirmationStep({ wizardState, event }: ConfirmationStepProps) {
  const cart = wizardState.cart;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-primary" />
          </div>
        </div>
        <h2 className="text-2xl font-heading font-bold text-primary">Registration Complete!</h2>
        <p className="text-muted-foreground">
          Thank you for registering. You will receive a confirmation email shortly.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Registration Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Event Info */}
          {event && (
            <div className="space-y-2 pb-4 border-b">
              <h3 className="font-semibold text-lg">{event.name}</h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                {formatDate(event.startDate)}
                {event.endDate !== event.startDate && ` - ${formatDate(event.endDate)}`}
              </div>
              {event.venue && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  {event.venue.name}, {event.venue.city}, {event.venue.state}
                </div>
              )}
            </div>
          )}

          {/* Order Items */}
          <div className="space-y-2">
            <h4 className="font-medium">Your Registrations</h4>
            {cart.items.map(item => (
              <div key={item.id} className="flex justify-between text-sm py-1">
                <span>
                  {item.name}
                  {item.quantity > 1 && ` (x${item.quantity})`}
                </span>
                <span>{formatCurrency(item.price * item.quantity)}</span>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="pt-4 border-t">
            <div className="flex justify-between font-bold">
              <span>Total Paid</span>
              <span>{formatCurrency(cart.total)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle>What's Next?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Check Your Email</p>
              <p className="text-sm text-muted-foreground">
                We've sent a confirmation email with your registration details.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Mark Your Calendar</p>
              <p className="text-sm text-muted-foreground">
                Add the event dates to your calendar so you don't forget!
              </p>
            </div>
          </div>

          {wizardState.tournaments.selectedIds.length > 0 && (
            <div className="flex items-start gap-3">
              <Download className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Review Tournament Rules</p>
                <p className="text-sm text-muted-foreground">
                  Make sure to review the rules for your registered tournaments.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-center gap-4">
        <Button variant="outline" onClick={() => window.print()}>
          Print Confirmation
        </Button>
        <Button onClick={() => window.location.href = '/'}>
          Return to Home
        </Button>
      </div>
    </div>
  );
}
